import { apiClient, getApiBaseUrl } from './api';
import { customerAuthService } from './customer-auth.service';

const getApiBase = () => getApiBaseUrl();

function getBookingAuthToken(): string | null {
  try {
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      if (session.expiresAt && Date.now() <= session.expiresAt) {
        return session.token || null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export interface Pass {
  id: string;
  name: string;
  description?: string;
  category: 'pass-green-fee' | 'pass-cart' | 'pass-range' | 'pass-pull-cart' | 'pass-other';
  includedProducts: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
  }>;
  totalQuantity: number;
  originalValue: number;
  passPrice: number;
  savingsAmount: number;
  savingsPercentage: number;
  assignsCustomerType: boolean;
  customerTypeId?: string;
  taxClassId?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  restrictions?: PassRestriction[];
  customerType?: {
    id: string;
    name: string;
    color: string;
  };
}

export interface PassRestriction {
  id: string;
  passProductId: string;
  restrictionType: 'date_range' | 'day_of_week' | 'time_range' | 'usage_limit';
  restrictionValue: string;
  status: string;
}

export interface CreatePassRequest {
  name: string;
  description?: string;
  category: 'pass-green-fee' | 'pass-cart' | 'pass-range' | 'pass-pull-cart' | 'pass-other';
  includedProducts: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
  }>;
  totalQuantity: number;
  originalValue: number;
  passPrice: number;
  assignsCustomerType?: boolean;
  customerTypeId?: string;
  taxClassId?: string;
  restrictions?: Array<{
    restrictionType: 'date_range' | 'day_of_week' | 'time_range' | 'usage_limit';
    restrictionValue: any;
  }>;
}

class PassesService {
  /**
   * Get all passes
   */
  async getAll(filters?: {
    category?: string;
    status?: string;
    search?: string;
  }): Promise<Pass[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const query = params.toString();
    const url = `/passes${query ? `?${query}` : ''}`;
    
    const response = await apiClient.get(url) as any;
    
    // The API returns the array directly, not wrapped in a data property
    return Array.isArray(response) ? response : (response.data || []);
  }

  /**
   * Get pass by ID
   */
  async getById(id: string): Promise<Pass> {
    const response = await apiClient.get(`/passes/${id}`) as any;
    return response.data;
  }

  /**
   * Create pass
   */
  async create(pass: CreatePassRequest): Promise<Pass> {
    const response = await apiClient.post('/passes', pass) as any;
    return response.data;
  }

  /**
   * Update pass
   */
  async update(id: string, pass: Partial<CreatePassRequest>): Promise<Pass> {
    const response = await apiClient.put(`/passes/${id}`, pass) as any;
    return response.data;
  }

  /**
   * Delete pass
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/passes/${id}`);
  }

  /**
   * Customer JWT: GET /passes/customers/passes/me (same payload as staff getCustomerPasses, includes recent usages).
   */
  async getMyPasses(status?: string): Promise<any[]> {
    const token = getBookingAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const q = params.toString();
    const url = `${getApiBase()}/passes/customers/passes/me${q ? `?${q}` : ''}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Staff/admin endpoint (`GET .../customers/passes?customerId=`).
   * In production the API expects an employee JWT; public booking flows should use {@link getMyPasses} instead.
   */
  async getCustomerPasses(customerId: string, status?: string): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('customerId', customerId);
    if (status) params.append('status', status);

    const query = params.toString();
    const url = `/passes/customers/passes?${query}`;
    
    // Get auth token and set it for this request
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        if (session.token) {
          apiClient.setAuthToken(session.token);
        }
      } catch {
        // If session parsing fails, continue without token; API will enforce auth
      }
    }
    
    const response = await apiClient.get(url) as any;
    // The API returns the array directly
    return Array.isArray(response) ? response : (response.data || []);
  }

  /**
   * Purchase pass for customer
   */
  async purchasePass(customerId: string, passId: string, paymentMethodId?: string): Promise<any> {
    const response = await apiClient.post(`/passes/customers/${customerId}/passes`, {
      passProductId: passId,
      paymentMethod: paymentMethodId
    }) as any;
    return response.data;
  }

  /**
   * Use pass for reservation
   */
  async usePass(customerId: string, passId: string, reservationId: string): Promise<any> {
    const response = await apiClient.post(`/passes/customers/${customerId}/passes/${passId}/use`, {
      reservationId
    }) as any;
    return response.data;
  }

  /**
   * Check if customer has valid pass for product
   */
  async checkPassAvailability(data: {
    productId: string;
    date?: string;
    time?: string;
  }): Promise<{ hasPass: boolean; passSubscription?: any }> {
    const params = new URLSearchParams();
    params.append('productId', data.productId);
    if (data.date) params.append('date', data.date);
    if (data.time) params.append('time', data.time);

    // Get auth token and set it for this request
    const customer = customerAuthService.getCachedCustomer();
    if (customer) {
      // Get token from localStorage
      const sessionData = localStorage.getItem('booking_service_auth');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        if (session.token) {
          apiClient.setAuthToken(session.token);
        }
      }
    }

    const response = await apiClient.get(`/passes/customers/passes/check?${params.toString()}`) as any;
    return response.data;
  }
}

export const passesService = new PassesService();
